import React from 'react';
import {
  pathStopViews,
  primaryPackId,
  chaptersSavedLine,
  stopStatusLine,
  unattributedLine,
} from '../utils/pathHome';

/**
 * Returning own-story home. One primary action (the existing seedless interview).
 * Eight spoken stops. A tap opens that stop's primary pack. No pack sheet.
 * Gold marks the trail only. Hot Seat is not on this screen.
 */
const PathHome = ({ progress, onJustTalk, onOpenPack, pipSrc = '/pip/pip-body.png' }) => {
  const stops = pathStopViews();
  const note = progress.attributionReady
    ? unattributedLine(progress.chapterCount, progress.unattributed)
    : '';

  const statusFor = (count) => (progress.attributionReady ? stopStatusLine(count) : '');

  return (
    <div className="path-home" data-testid="path-home">
      <style>{`
        .path-home {
          min-height: calc(100vh - 150px);
          background: #FBF8F1;
          color: #0F172A;
          font-family: 'Lora', Georgia, serif;
          padding: 8px 16px 28px;
          box-sizing: border-box;
        }
        .path-wrap { width: 100%; max-width: 430px; margin: 0 auto; }
        .path-mark {
          display: flex; align-items: center; justify-content: center; gap: 10px;
          color: #8A6420; margin-top: 6px;
        }
        .path-mark .rule { width: 22px; height: 1px; background: rgba(184,129,62,.55); }
        .path-mark .name { font-size: 14px; letter-spacing: .22em; text-transform: uppercase; font-weight: 600; }
        .path-pip { display: block; height: 88px; width: auto; margin: 8px auto 0; }
        .path-count {
          margin: 8px 0 0; text-align: center;
          font-size: 28px; line-height: 1.25; font-weight: 600; font-style: italic;
        }
        .path-note {
          margin: 8px 0 0; text-align: center;
          font-size: 16px; line-height: 1.45; color: #33404F;
        }
        .path-talk {
          width: 100%; margin-top: 16px;
          background: linear-gradient(180deg,#AE6015,#A85A0F); color: #fff;
          border: none; border-radius: 18px; padding: 18px 16px;
          font-family: 'Lora', Georgia, serif; font-size: 22px; font-weight: 700;
          box-shadow: 0 4px 16px rgba(168,90,15,.3); cursor: pointer;
          -webkit-tap-highlight-color: transparent;
        }
        .path-talk:active { transform: scale(.98); }
        .path-reassure {
          margin: 10px 4px 0; text-align: center;
          font-size: 16px; line-height: 1.45; color: #33404F;
        }
        .path-h {
          margin: 22px 0 12px; text-align: center;
          font-size: 22px; line-height: 1.3; font-weight: 600; font-style: italic;
        }
        .path-trail { position: relative; padding-left: 28px; }
        .path-rail {
          position: absolute; left: 8px; top: 18px; bottom: 18px; width: 3px;
          background: #E8DCC8; border-radius: 2px;
        }
        .path-stop {
          width: 100%; text-align: left; cursor: pointer;
          font-family: 'Lora', Georgia, serif; color: #0F172A;
          background: #FFFDF8; border: 1px solid rgba(184,129,62,.45);
          border-radius: 16px; padding: 14px 14px 14px 16px;
          -webkit-tap-highlight-color: transparent;
        }
        .path-stop { position: relative; margin: 0 0 12px; min-height: 72px; }
        .path-stop.saved { background: #F7EED4; border-color: #C9A84C; }
        .path-dot {
          position: absolute; left: -28px; top: 22px; width: 16px; height: 16px;
          border-radius: 50%; box-sizing: border-box;
          background: #FFFDF8; border: 2px solid #8A6420;
        }
        .path-stop.saved .path-dot { background: #C9A84C; border-color: #8A6420; }
        .path-name { display: block; font-size: 18px; line-height: 1.3; font-weight: 600; }
        .path-blurb, .path-status { display: block; margin-top: 4px; font-size: 16px; line-height: 1.4; color: #33404F; }
      `}</style>

      <div className="path-wrap">
        <div className="path-mark">
          <span className="rule" />
          <span className="name">Talebound</span>
          <span className="rule" />
        </div>
        <img className="path-pip" src={pipSrc} alt="Pip the fox" />
        <p className="path-count" data-testid="path-count">{chaptersSavedLine(progress.chapterCount)}</p>
        {note ? <p className="path-note" data-testid="path-unattributed">{note}</p> : null}

        <button type="button" className="path-talk" data-testid="path-just-talk" onClick={onJustTalk}>
          Just start talking
        </button>
        <p className="path-reassure">Like talking with a friend. No need to pick a question; just start anywhere.</p>

        <h2 className="path-h">What do you feel like talking about?</h2>
        <div className="path-trail">
          <div className="path-rail" aria-hidden="true" />
          {stops.map(stop => {
            const count = progress.stopCounts[stop.id] || 0;
            const status = statusFor(count);
            const saved = progress.attributionReady && count > 0;
            return (
              <button
                type="button"
                key={stop.id}
                className={saved ? 'path-stop saved' : 'path-stop'}
                data-testid={`path-stop-${stop.id}`}
                onClick={() => onOpenPack(primaryPackId(stop.id))}
              >
                <span className="path-dot" aria-hidden="true" />
                <span className="path-name">{stop.label}</span>
                {stop.packs.length === 1 ? <span className="path-blurb">{stop.packs[0].blurb}</span> : null}
                {status ? <span className="path-status">{status}</span> : null}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PathHome;
